/**
 * Application shell: owns the time window, the selected spacecraft, and the shared clock that
 * keeps the globe, the body view, the event bands and the strip charts pointing at the same
 * instant.
 *
 * Time policy (handover 4.1): everything the UI shows or accepts is UTC. The datetime-local
 * inputs are formatted from UTC components and parsed back as UTC explicitly - Date's local-time
 * behaviour is never relied on, so the loaded window is identical in every browser timezone.
 */

import * as Cesium from 'cesium';
import * as THREE from 'three';

import { ApiError, api, iso, requestSignal } from './api/client';
import type {
  RpyCrossCheck,
  Spacecraft,
  SunSensorCheck,
  TimeWindow,
  TimelineEvent,
} from './api/types';
import { BodyView, type BodyViewData } from './body/bodyView';
import { GlobeView } from './globe/globe';
import { DEFAULT_STRIPS, StripCharts } from './plots/strips';
import { EventBands } from './timeline/eventBands';

interface BodyViewResponse {
  frame: string;
  attitude_source: string;
  notes: string[];
  count: number;
  times: string[];
  valid: boolean[];
  quat_eci_from_body: number[][];
  sun_eci: number[][];
  nadir_eci: number[][];
  velocity_eci: number[][];
  b_eci: number[][];
  omega_eci: number[][];
  omega_magnitude_deg_s: number[];
  eclipse_state: number[];
}

/** What one panel loader found: did the window yield telemetry, and did the request fail? */
interface LoadOutcome {
  hasData: boolean;
  failed: boolean;
}

type PanelKey = 'globe' | 'body' | 'bands' | 'strips';

const ECLIPSE_LABELS = ['sunlit', 'penumbra', 'umbra'];

export class App {
  private globe!: GlobeView;
  private body!: BodyView;
  private bands!: EventBands;
  private strips!: StripCharts;

  private spacecraft: Spacecraft[] = [];
  private selected = new Set<string>();
  private primary = '';
  private demo = false;

  private bodyData: BodyViewData | null = null;
  private bodyRaw: BodyViewResponse | null = null;
  private events: TimelineEvent[] = [];
  private window: TimeWindow = defaultWindow();
  private suppressClockSync = false;

  private readonly focusSelects: HTMLSelectElement[] = [];
  /** First/last archived sample per spacecraft, from `GET /{sat}/coverage` (epoch ms). */
  private readonly coverageRange = new Map<string, { first: number; last: number }>();
  /**
   * Generation counter for loads. Load is not re-entrant: pressing Load twice, clicking chips
   * or changing focus mid-load starts a new load, and responses from the superseded one must
   * never reach the panels - otherwise two loads interleave and leave panels from different
   * windows or spacecraft mixed together.
   */
  private loadSeq = 0;

  async start(): Promise<void> {
    this.readUrlWindow();
    this.bindChrome();

    const [health, list] = await Promise.all([
      api.health().catch(() => null),
      api.spacecraft(),
    ]);
    this.demo = list.demo;
    this.spacecraft = list.spacecraft;
    this.applyUrlSelection(true);

    this.renderSatChips();
    this.renderBanner(health?.yamcs?.reachable === false ? health.yamcs.error : undefined);

    this.globe = await GlobeView.create({
      container: document.getElementById('globe')!,
      onTick: (time) => this.onClockTick(time),
    });
    this.body = new BodyView(document.getElementById('bodyview')!);
    this.bands = new EventBands({
      container: document.getElementById('bands')!,
      onSeek: (ms) => this.seek(ms),
    });
    this.strips = new StripCharts({
      container: document.getElementById('strips')!,
      onSeek: (ms) => this.seek(ms),
    });
    this.buildStripToggles();

    this.bindToggles();
    window.addEventListener('resize', () => this.resize());

    // A shared link pasted into an already-open tab only changes the hash: same-document
    // navigation, no reload. Follow it, so the shared view actually restores.
    // (Our own updateUrlState uses replaceState, which does not fire this event.)
    window.addEventListener('hashchange', () => {
      this.readUrlWindow();
      this.applyUrlSelection(false);
      this.syncWindowInputs();
      this.renderSatChips();
      void this.load();
    });

    // Coverage bounds the time picker and powers the "jump to data" action on empty windows.
    // It is a hint, not a prerequisite: the first load must not wait on it.
    void this.loadCoverage();

    await this.load();
  }

  // -------------------------------------------------------------------------------- chrome

  private bindChrome(): void {
    const startInput = document.getElementById('start') as HTMLInputElement;
    const stopInput = document.getElementById('stop') as HTMLInputElement;
    this.syncWindowInputs();

    document.getElementById('preset')!.addEventListener('change', (e) => {
      const hours = Number((e.target as HTMLSelectElement).value);
      if (!Number.isFinite(hours) || hours <= 0) return;
      // Both inputs are UTC; an empty or invalid "to" falls back to now. The preset rewrites
      // BOTH fields - the previous version left `stop` untouched, which silently mixed the
      // typed stop with a preset start.
      const stop = parseUtcInput(stopInput.value) ?? roundToMinute(new Date());
      const start = new Date(stop.getTime() - hours * 3600_000);
      startInput.value = toUtcInput(start);
      stopInput.value = toUtcInput(stop);
    });

    document.getElementById('load')!.addEventListener('click', () => {
      const start = parseUtcInput(startInput.value);
      const stop = parseUtcInput(stopInput.value);
      if (!start || !stop) {
        this.toast('Both dates must be valid UTC times (YYYY-MM-DDTHH:MM[:SS]).', 'error');
        return;
      }
      if (!(start < stop)) {
        this.toast('The end of the window must be after its start.', 'error');
        return;
      }
      this.window = { start, stop };
      void this.load();
    });

    document.getElementById('diagnose')!.addEventListener('click', () => void this.showDiagnostics());

    for (const id of ['focus-body', 'focus-bands', 'focus-strips']) {
      const select = document.getElementById(id) as HTMLSelectElement | null;
      if (!select) continue;
      this.focusSelects.push(select);
      select.addEventListener('change', () => this.setPrimary(select.value));
    }

    document.getElementById('theme')!.addEventListener('click', () => {
      const root = document.documentElement;
      const next = root.dataset.theme === 'light' ? 'dark' : 'light';
      root.dataset.theme = next;
      localStorage.setItem('phasma-theme', next);
      // The strips paint their axes into a canvas, so a CSS variable alone cannot reach them.
      // Guarded because the theme button is bound before the panels are constructed.
      if (this.strips) this.strips.setTheme();
    });
    // Applied before the panels are built, so they read the restored theme on first paint.
    const savedTheme = localStorage.getItem('phasma-theme');
    if (savedTheme) document.documentElement.dataset.theme = savedTheme;

    const dialog = document.getElementById('dialog') as HTMLDialogElement;
    document.getElementById('dialog-close')!.addEventListener('click', () => dialog.close());
  }

  private syncWindowInputs(): void {
    (document.getElementById('start') as HTMLInputElement).value = toUtcInput(this.window.start);
    (document.getElementById('stop') as HTMLInputElement).value = toUtcInput(this.window.stop);
  }

  /** Change the spacecraft the single-focus panels (body view, bands, strips) show. */
  private setPrimary(key: string): void {
    if (!this.spacecraft.some((s) => s.key === key) || key === this.primary) {
      this.syncFocusControls();
      return;
    }
    this.primary = key;
    this.selected.add(key);
    this.renderSatChips();
    void this.load();
  }

  /** Keep every per-panel spacecraft selector populated and pointing at the focus spacecraft. */
  private syncFocusControls(): void {
    for (const select of this.focusSelects) {
      select.replaceChildren();
      for (const sat of this.spacecraft) {
        const option = document.createElement('option');
        option.value = sat.key;
        option.textContent = sat.name;
        select.appendChild(option);
      }
      select.value = this.primary;
    }
  }

  private renderSatChips(): void {
    const host = document.getElementById('sat-toggles')!;
    host.replaceChildren();
    for (const sat of this.spacecraft) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sat-chip';
      chip.setAttribute('aria-pressed', String(this.selected.has(sat.key)));
      chip.title = `${sat.name} — NORAD ${sat.norad_cat_id}${
        sat.norad_verified ? '' : ' (unverified)'
      }. Click to show/hide on the globe. Each panel's own selector chooses which spacecraft it shows.`;
      chip.innerHTML = `<span class="dot" style="background:${sat.colour}"></span>${sat.name.replace(
        'PHASMA-',
        '',
      )}${this.primary === sat.key ? '<span class="primary-mark">focus</span>' : ''}`;
      chip.addEventListener('click', () => {
        if (!this.selected.has(sat.key)) {
          this.selected.add(sat.key);
        } else if (this.selected.size > 1) {
          this.selected.delete(sat.key);
          if (this.primary === sat.key) this.primary = [...this.selected][0]!;
        } else {
          // The last selected spacecraft: deselecting it would blank every panel. Make the
          // click harmless instead - it keeps the spacecraft selected.
          return;
        }
        this.renderSatChips();
        void this.load();
      });
      host.appendChild(chip);
    }
    this.syncFocusControls();
  }

  private renderBanner(yamcsError?: string): void {
    const banner = document.getElementById('banner')!;
    const messages: string[] = [];
    let severity = 'warn';

    if (this.demo) {
      messages.push(
        'DEMO MODE — telemetry and element sets are synthetic, not mission data. Set YAMCS_URL and credentials to use the real archive.',
      );
    } else if (yamcsError) {
      severity = 'error';
      // Not necessarily unreachable — a rejected login reports the server just fine. The
      // backend's message says which it is, so do not second-guess it here.
      messages.push(`YAMCS is unavailable: ${yamcsError}`);
    }

    const unverified = this.spacecraft.filter((s) => !s.norad_verified).map((s) => s.name);
    if (unverified.length) {
      messages.push(
        `NORAD ids unverified for ${unverified.join(', ')} — ground tracks are only as good as those ids. Run \`import_tles.py verify\`.`,
      );
    }
    const noTles = this.spacecraft.filter((s) => s.tle_archive.count === 0).map((s) => s.name);
    if (noTles.length && !this.demo) {
      severity = 'error';
      messages.push(`No TLEs held for ${noTles.join(', ')} — backfill the archive before replaying.`);
    }

    banner.hidden = messages.length === 0;
    banner.dataset.severity = severity;
    banner.textContent = messages.join('  •  ');
  }

  private bindToggles(): void {
    const bind = (id: string, fn: (checked: boolean) => void): void => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      el?.addEventListener('change', () => fn(el.checked));
    };
    bind('t-path', (v) => {
      for (const key of this.selected) this.globe.setPathVisible(`sat/${key}`, v);
    });
    bind('t-track', (v) => this.globe.setGroundTracksVisible(v));
    bind('t-eclipse', (v) => this.globe.setEclipseVisible(v));
    bind('t-axes', (v) => this.body.setToggles({ bodyAxes: v }));
    bind('t-omega', (v) => this.body.setToggles({ omega: v }));
    bind('t-trace', (v) => this.body.setToggles({ spinTrace: v }));
    bind('t-sun', (v) => this.body.setToggles({ sun: v }));
    bind('t-mag', (v) => this.body.setToggles({ mag: v }));
    bind('t-nadir', (v) => this.body.setToggles({ nadir: v }));
    bind('t-vel', (v) => this.body.setToggles({ velocity: v }));
    bind('t-solar', (v) => this.body.setToggles({ solar: v }));
  }

  // ----------------------------------------------------------------------- URL state (5.2)

  /** Restore the time window from the URL hash, if present and sane. */
  private readUrlWindow(): void {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const start = params.get('start');
    const stop = params.get('stop');
    if (!start || !stop) return;
    const s = new Date(start);
    const e = new Date(stop);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || !(s < e)) return;
    this.window = { start: s, stop: e };
  }

  /**
   * Restore the selected spacecraft and focus from the URL hash (needs the spacecraft list).
   * With `useDefaults` (initial load) a hash without selection params means "everything
   * selected"; without it (hashchange while running) the current selection is kept.
   */
  private applyUrlSelection(useDefaults: boolean): void {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const known = new Set(this.spacecraft.map((s) => s.key));
    const hasSelectionParams = params.has('sats') || params.has('focus');

    if (hasSelectionParams || useDefaults) {
      const sats = (params.get('sats') ?? '').split(',').filter((key) => known.has(key));
      this.selected = new Set(sats.length ? sats : this.spacecraft.map((s) => s.key));
      const focus = params.get('focus') ?? '';
      if (known.has(focus)) {
        this.primary = focus;
        this.selected.add(focus);
      } else {
        this.primary = [...this.selected][0] ?? this.spacecraft[0]?.key ?? '';
      }
    }
    if (!this.selected.has(this.primary)) this.primary = [...this.selected][0] ?? '';
  }

  /** Encode window + selection + focus in the hash so a view is shareable. No history spam. */
  private updateUrlState(): void {
    const params = new URLSearchParams();
    params.set('start', iso(this.window.start));
    params.set('stop', iso(this.window.stop));
    params.set('sats', [...this.selected].join(','));
    params.set('focus', this.primary);
    const hash = `#${params.toString()}`;
    if (window.location.hash !== hash) {
      history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}${hash}`,
      );
    }
  }

  // ------------------------------------------------------------------ coverage / empty (4.3)

  private async loadCoverage(): Promise<void> {
    // The API rejects windows longer than 31 days on every endpoint, coverage included, so the
    // query is limited to the recent past. That is still the useful range for this feature:
    // its job is to find the data nearest to *now* and offer a jump to it.
    const recent: TimeWindow = {
      start: new Date(Date.now() - 28 * 24 * 3600_000),
      stop: new Date(Date.now() + 3600_000),
    };
    await Promise.all(
      this.spacecraft.map(async (sat) => {
        try {
          const coverage = await api.coverage(sat.key, recent);
          let first = Infinity;
          let last = -Infinity;
          for (const packet of coverage.packets) {
            if (packet.first) first = Math.min(first, Date.parse(packet.first));
            if (packet.last) last = Math.max(last, Date.parse(packet.last));
          }
          if (first <= last) this.coverageRange.set(sat.key, { first, last });
        } catch {
          // Coverage only powers hints; a failure here must never block replay.
        }
      }),
    );

    const ranges = [...this.coverageRange.values()];
    if (ranges.length === 0) return;
    const min = Math.min(...ranges.map((r) => r.first));
    const max = Math.max(...ranges.map((r) => r.last));
    const startInput = document.getElementById('start') as HTMLInputElement;
    const stopInput = document.getElementById('stop') as HTMLInputElement;
    startInput.min = toUtcInput(new Date(min));
    startInput.max = toUtcInput(new Date(max));
    stopInput.min = toUtcInput(new Date(min));
    stopInput.max = toUtcInput(new Date(max));
  }

  /**
   * When a window yields no telemetry at all (and nothing failed), say so plainly and offer a
   * jump to the nearest coverage of the focus spacecraft. An empty window must never look like
   * a broken tool.
   *
   * `hasTelemetry` must reflect telemetry-bearing panels only (body view, strips). The event
   * bands are not evidence: illumination bands are computed from the orbit, so they exist even
   * in a window with zero telemetry, and counting them would suppress this notice forever.
   */
  private updateEmptyNotice(hasTelemetry: boolean, failures: number): void {
    const host = document.getElementById('empty-notice');
    if (!host) return;
    if (hasTelemetry || failures > 0) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }

    const satName = this.spacecraft.find((s) => s.key === this.primary)?.name ?? this.primary;
    host.replaceChildren();
    const message = document.createElement('span');
    message.textContent = `No telemetry for ${satName} in this window — the archive simply has no samples here; nothing failed.`;
    host.appendChild(message);

    const range = this.coverageRange.get(this.primary);
    if (range) {
      host.appendChild(this.jumpButton('Jump to earliest data', range, 'first'));
      host.appendChild(this.jumpButton('Jump to latest data', range, 'last'));
    } else {
      const hint = document.createElement('span');
      hint.className = 'empty-notice-hint';
      hint.textContent = 'Telemetry coverage is unavailable, so no data window can be suggested.';
      host.appendChild(hint);
    }

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'empty-dismiss';
    dismiss.textContent = 'Dismiss';
    dismiss.addEventListener('click', () => {
      host.hidden = true;
    });
    host.appendChild(dismiss);
    host.hidden = false;
  }

  private jumpButton(
    label: string,
    range: { first: number; last: number },
    which: 'first' | 'last',
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', () => this.jumpToCoverage(range, which));
    return button;
  }

  private jumpToCoverage(range: { first: number; last: number }, which: 'first' | 'last'): void {
    const DAY = 24 * 3600_000;
    let start: number;
    let stop: number;
    if (range.last - range.first < DAY) {
      // Sparse archive: show everything there is, padded to a readable span.
      start = range.first - 3600_000;
      stop = range.last + 3600_000;
    } else if (which === 'first') {
      start = range.first;
      stop = range.first + DAY;
    } else {
      start = range.last - DAY;
      stop = range.last;
    }
    this.window = { start: new Date(start), stop: new Date(stop) };
    this.syncWindowInputs();
    void this.load();
  }

  // ------------------------------------------------------------------- panel status (4.4, 5.1)

  private panelHost(panel: PanelKey): HTMLElement {
    const selector =
      panel === 'globe'
        ? '.pane-globe'
        : panel === 'body'
          ? '.pane-body'
          : `#section-${panel}`;
    return document.querySelector<HTMLElement>(selector)!;
  }

  private panelStack(panel: PanelKey): HTMLElement {
    const host = this.panelHost(panel);
    let stack = host.querySelector<HTMLElement>(':scope > .panel-stack');
    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'panel-stack';
      host.appendChild(stack);
    }
    return stack;
  }

  /** Show (or clear) a loading indicator pinned to a panel. */
  private setPanelLoading(panel: PanelKey, on: boolean): void {
    const stack = this.panelStack(panel);
    let el = stack.querySelector<HTMLElement>(':scope > .panel-loading');
    if (!on) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement('div');
      el.className = 'panel-loading';
      el.setAttribute('role', 'status');
      el.textContent = 'Loading…';
      stack.prepend(el);
    }
  }

  /**
   * Show (or clear) a persistent error pinned to a panel. Errors stay until the operator
   * dismisses them or the panel loads successfully - never a vanishing toast (handover 4.4).
   */
  private setPanelError(panel: PanelKey, message: string | null): void {
    const stack = this.panelStack(panel);
    let el = stack.querySelector<HTMLElement>(':scope > .panel-error');
    if (message === null) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement('div');
      el.className = 'panel-error';
      el.setAttribute('role', 'alert');
      const text = document.createElement('span');
      text.className = 'panel-error-msg';
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'panel-error-dismiss';
      dismiss.setAttribute('aria-label', 'Dismiss error');
      dismiss.textContent = '×';
      dismiss.addEventListener('click', () => el?.remove());
      el.append(text, dismiss);
      stack.appendChild(el);
    }
    el.querySelector('.panel-error-msg')!.textContent = message;
  }

  /** Surface backend caveats (extrapolated orbit, integrated attitude, …) under a pane header. */
  private setCaveat(panel: 'globe' | 'body', notes: string[]): void {
    const el = document.getElementById(`${panel}-caveat`);
    if (!el) return;
    el.hidden = notes.length === 0;
    el.textContent = notes.join('  •  ');
  }

  // ---------------------------------------------------------------------------------- load

  private async load(): Promise<void> {
    const keys = [...this.selected];
    if (keys.length === 0) return;
    const seq = ++this.loadSeq;
    const stale = (): boolean => seq !== this.loadSeq;
    const done = this.toast('Loading replay…', 'busy', 0);
    // Everything below runs inside try/finally: the busy toast is the one indicator with no
    // timeout, so any exception that escaped (or a panel loader that threw synchronously)
    // previously left "Loading replay…" spinning forever with nothing else to act on.
    try {
      const stepSeconds = chooseStep(this.window);

      // The replay clock follows the requested window even if the CZML fetch fails, so the
      // bands and strips still scrub against the intended range.
      this.globe.clock.startTime = Cesium.JulianDate.fromDate(this.window.start);
      this.globe.clock.stopTime = Cesium.JulianDate.fromDate(this.window.stop);
      this.globe.clock.currentTime = Cesium.JulianDate.fromDate(this.window.start);
      this.globe.clock.clockRange = Cesium.ClockRange.CLAMPED;
      this.globe.viewer.timeline?.zoomTo(this.globe.clock.startTime, this.globe.clock.stopTime);
      this.bands.setWindow(this.window.start.getTime(), this.window.stop.getTime());
      this.strips.setWindow(this.window.start.getTime(), this.window.stop.getTime());

      // All four panels are fetched together. The globe used to be awaited first, which put the
      // whole CZML round trip on the critical path before the other three were even requested -
      // measured against production on 2026-08-11, 8.3 s of a 34 s cold load, and 2.6 s of a
      // warm one. Nothing below depends on the document: the replay clock is already set from
      // the requested window above.
      const [globeOutcome, bodyOutcome, eventsOutcome, stripsOutcome] = await Promise.all([
        this.loadGlobe(seq, keys, stepSeconds),
        this.loadBodyView(seq),
        this.loadEvents(seq),
        this.loadStrips(seq),
      ]);
      if (stale()) return;
      const failures = [globeOutcome, bodyOutcome, eventsOutcome, stripsOutcome].filter(
        (o) => o.failed,
      ).length;
      // Telemetry evidence comes from the body view and the strips only; event bands include
      // orbit-derived illumination and exist even with zero telemetry (see updateEmptyNotice).
      const hasTelemetry = bodyOutcome.hasData || stripsOutcome.hasData;

      this.resize();
      this.updateEmptyNotice(hasTelemetry, failures);
      this.updateUrlState();
      this.toast(`Loaded ${keys.length === 1 ? keys[0] : `${keys.length} spacecraft`}.`, 'info');
    } finally {
      done();
    }
  }

  /**
   * Load the replay document into the globe.
   *
   * `hasData` is deliberately always false: the CZML carries position, ground track and eclipse,
   * all of which are computed from the orbit and therefore exist in a window with no telemetry
   * whatsoever. Counting them as data would suppress the empty-window notice forever, for the
   * same reason the event bands do not count (see updateEmptyNotice).
   */
  private async loadGlobe(seq: number, keys: string[], stepSeconds: number): Promise<LoadOutcome> {
    this.setPanelLoading('globe', true);
    const stale = (): boolean => seq !== this.loadSeq;
    try {
      // Fetched here rather than by Cesium so response-level caveats (the `notes` packets,
      // e.g. extrapolated-orbit warnings) are surfaced instead of discarded (handover 4.3).
      const { document: czml, notes } = await fetchCzml(
        api.czmlUrl(keys, this.window, stepSeconds),
      );
      // A newer load has started: drop this document instead of swapping it into the viewer.
      if (stale()) return { hasData: false, failed: false };
      await this.globe.loadCzml(czml);
      // Deliberately not awaited: zoomTo resolves only once Cesium has rendered the camera
      // flight, so awaiting it stalls the whole load whenever the canvas is not compositing -
      // a background tab, a hidden pane, a headless check. The data does not depend on it.
      void this.globe.zoomToAll().catch(() => undefined);
      this.setPanelError('globe', null);
      this.setCaveat('globe', notes);
      return { hasData: false, failed: false };
    } catch (error) {
      if (stale()) return { hasData: false, failed: false };
      this.setPanelError('globe', formatError(error));
      this.setCaveat('globe', []);
      return { hasData: false, failed: true };
    } finally {
      this.setPanelLoading('globe', false);
    }
  }

  private async loadBodyView(seq: number): Promise<LoadOutcome> {
    this.setPanelLoading('body', true);
    const discarded: LoadOutcome = { hasData: false, failed: false };
    const stale = (): boolean => seq !== this.loadSeq;
    const fail = (message: string): LoadOutcome => {
      if (stale()) return discarded;
      this.bodyData = null;
      this.bodyRaw = null;
      this.body.clear();
      document.getElementById('body-frame')!.textContent = 'unavailable';
      this.setCaveat('body', []);
      this.setPanelError('body', message);
      return { hasData: false, failed: true };
    };

    try {
      const step = Math.max(5, Math.round(chooseStep(this.window) / 3));
      const url = new URL(`/api/v1/${this.primary}/body_view`, window.location.origin);
      url.searchParams.set('start', iso(this.window.start));
      url.searchParams.set('stop', iso(this.window.stop));
      url.searchParams.set('step', String(step));

      const response = await fetch(url, { signal: requestSignal() });
      if (!response.ok) return fail(`body_view failed (${response.status}) for ${this.primary}.`);

      const raw = (await response.json()) as BodyViewResponse;
      if (stale()) return discarded;
      const data: BodyViewData = {
        times: raw.times.map((t) => Date.parse(t)),
        quatEciFromBody: raw.quat_eci_from_body.map(
          (q) => new THREE.Quaternion(q[0]!, q[1]!, q[2]!, q[3]!),
        ),
        sunEci: raw.sun_eci.map(toVector),
        nadirEci: raw.nadir_eci.map(toVector),
        velocityEci: raw.velocity_eci.map(toVector),
        bEci: raw.b_eci.map(toVector),
        omegaEci: raw.omega_eci.map(toVector),
        omegaMagnitudeDegS: raw.omega_magnitude_deg_s,
        valid: raw.valid,
        attitudeSource: raw.attitude_source,
      };
      this.bodyData = data;
      this.body.setData(data);
      this.bodyRaw = raw;
      // Solar string currents for the panel tinting (deliberately fire-and-forget: the body
      // view is complete without them, and the backend's hot memo makes the fetch cheap).
      void this.loadSolarOverlay(seq);

      const label =
        raw.attitude_source === 'integrated' ? 'inertial (ECI) · INTEGRATED' : 'inertial (ECI)';
      document.getElementById('body-frame')!.textContent = label;

      // Caveats persist under the pane header instead of vanishing as a toast.
      const notes = [...(raw.notes ?? [])];
      if (
        raw.attitude_source === 'integrated' &&
        !notes.some((n) => n.toLowerCase().includes('integrated'))
      ) {
        notes.unshift(
          'Attitude is integrated from gyro rates, not estimator output — it is relative, not absolute.',
        );
      }
      this.setCaveat('body', notes);
      this.setPanelError('body', null);

      // Orbit products always exist, so `count` says nothing about telemetry. Presence means a
      // valid attitude sample or a non-zero rate somewhere in the window.
      const hasData =
        raw.valid.some(Boolean) || raw.omega_magnitude_deg_s.some((v) => v > 0);
      return { hasData, failed: false };
    } catch (error) {
      return fail(formatError(error));
    } finally {
      this.setPanelLoading('body', false);
    }
  }

  /** Fetch the per-string solar currents that drive the body view's panel tinting. */
  private async loadSolarOverlay(seq: number): Promise<void> {
    try {
      const response = await api.series(this.primary, 'power_tlm', this.window, 4000);
      if (seq !== this.loadSeq) return;
      const currents: Record<number, (number | null)[]> = {};
      for (let bcr = 1; bcr <= 7; bcr++) {
        const series = response.series[`ibcr${bcr}`];
        if (series) currents[bcr] = series;
      }
      this.body.setSolarData({
        times: response.times.map((t) => Date.parse(t)),
        currents,
      });
    } catch {
      if (seq === this.loadSeq) this.body.setSolarData(null);
    }
  }

  private async loadEvents(seq: number): Promise<LoadOutcome> {
    this.setPanelLoading('bands', true);
    try {
      const response = await api.events(this.primary, this.window);
      if (seq !== this.loadSeq) return { hasData: false, failed: false };
      this.events = response.events;
      this.bands.setEvents(this.events);
      this.setPanelError('bands', null);
      return { hasData: this.events.length > 0, failed: false };
    } catch (error) {
      if (seq !== this.loadSeq) return { hasData: false, failed: false };
      this.events = [];
      this.bands.setEvents([]);
      this.setPanelError('bands', formatError(error));
      return { hasData: false, failed: true };
    } finally {
      this.setPanelLoading('bands', false);
    }
  }

  /**
   * Which strips the operator wants to see, persisted across sessions. Hidden strips are not
   * fetched at all - toggling one on triggers its own fetch - so growing the catalogue of
   * available strips (EPS, solar strings, ...) costs nothing until someone opens them.
   */
  private stripSelection(): Set<string> {
    try {
      const saved = localStorage.getItem('phasma-strips');
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch {
      /* fall through to defaults */
    }
    return new Set(DEFAULT_STRIPS.filter((d) => d.defaultOn !== false).map((d) => d.key));
  }

  private buildStripToggles(): void {
    const header = document.querySelector('#section-strips .section-header');
    if (!header) return;
    const selection = this.stripSelection();
    const bar = document.createElement('div');
    bar.className = 'strip-toggles';
    for (const definition of DEFAULT_STRIPS) {
      const label = document.createElement('label');
      label.className = 'strip-toggle';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = selection.has(definition.key);
      box.addEventListener('change', () => {
        const current = this.stripSelection();
        if (box.checked) current.add(definition.key);
        else current.delete(definition.key);
        localStorage.setItem('phasma-strips', JSON.stringify([...current]));
        if (box.checked) {
          void this.loadOneStrip(definition.key);
        } else {
          this.strips.remove(definition.key);
        }
      });
      label.append(box, document.createTextNode(definition.title));
      bar.appendChild(label);
    }
    header.appendChild(bar);
  }

  private async loadOneStrip(key: string): Promise<void> {
    const definition = DEFAULT_STRIPS.find((d) => d.key === key);
    if (!definition) return;
    try {
      const response = await api.series(this.primary, definition.dataset, this.window, 4000);
      this.strips.update(definition, response);
    } catch (error) {
      const detail = error instanceof ApiError ? (error.detail ?? error.message) : String(error);
      this.strips.update(definition, null, detail);
    }
    this.resize();
  }

  private async loadStrips(seq: number): Promise<LoadOutcome> {
    this.setPanelLoading('strips', true);
    const stale = (): boolean => seq !== this.loadSeq;
    const selection = this.stripSelection();
    const visible = DEFAULT_STRIPS.filter((d) => selection.has(d.key));
    try {
      const outcomes = await Promise.all(
        visible.map(async (definition) => {
          try {
            const response = await api.series(this.primary, definition.dataset, this.window, 4000);
            if (stale()) return { dataset: definition.dataset, ok: true, hasData: false };
            this.strips.update(definition, response);
            return { dataset: definition.dataset, ok: true, hasData: response.count > 0 };
          } catch (error) {
            if (stale()) return { dataset: definition.dataset, ok: false, hasData: false };
            const detail =
              error instanceof ApiError ? (error.detail ?? error.message) : String(error);
            this.strips.update(definition, null, detail);
            return { dataset: definition.dataset, ok: false, hasData: false };
          }
        }),
      );
      if (stale()) return { hasData: false, failed: false };
      const failures = outcomes.filter((o) => !o.ok);
      if (failures.length > 0) {
        this.setPanelError(
          'strips',
          `Failed to load: ${failures.map((f) => f.dataset).join(', ')}.`,
        );
      } else {
        this.setPanelError('strips', null);
      }
      return {
        hasData: outcomes.some((o) => o.hasData),
        failed: failures.length === outcomes.length && outcomes.length > 0,
      };
    } finally {
      this.setPanelLoading('strips', false);
    }
  }

  // --------------------------------------------------------------------------------- clock

  private onClockTick(time: Cesium.JulianDate): void {
    if (this.suppressClockSync) return;
    const ms = Cesium.JulianDate.toDate(time).getTime();
    this.body.setTime(ms);
    this.bands.setPlayhead(ms);
    this.strips.setPlayhead(ms);
    this.updateReadout(ms);
  }

  private seek(epochMs: number): void {
    this.suppressClockSync = true;
    this.globe.clock.currentTime = Cesium.JulianDate.fromDate(new Date(epochMs));
    this.suppressClockSync = false;
    this.body.setTime(epochMs);
    this.bands.setPlayhead(epochMs);
    this.strips.setPlayhead(epochMs);
    this.updateReadout(epochMs);
  }

  private updateReadout(epochMs: number): void {
    const host = document.getElementById('readout')!;
    const rows: [string, string, boolean?][] = [
      ['Time', new Date(epochMs).toISOString().replace('T', ' ').replace(/\.\d{3}Z/, ' UTC')],
    ];

    const raw = this.bodyRaw;
    const data = this.bodyData;
    if (data && raw) {
      const i = this.body.sampleIndex;
      const valid = data.valid[i] ?? false;
      rows.push(['Attitude', valid ? raw.attitude_source : 'no data', !valid]);
      rows.push(['|omega|', `${(data.omegaMagnitudeDegS[i] ?? 0).toFixed(3)} deg/s`]);
      const eclipse = raw.eclipse_state[i] ?? 0;
      rows.push(['Illumination', ECLIPSE_LABELS[eclipse] ?? '?', eclipse === 2]);
      const omega = data.omegaEci[i];
      if (omega && valid) {
        rows.push([
          'Spin axis (ECI)',
          `${omega.x.toFixed(3)}, ${omega.y.toFixed(3)}, ${omega.z.toFixed(3)}`,
        ]);
      }
    } else {
      rows.push(['Attitude', 'unavailable', true]);
    }

    const active = this.events.filter((event) => {
      if (!event.end_time) return false;
      const start = Date.parse(event.time);
      const end = Date.parse(event.end_time);
      return epochMs >= start && epochMs <= end && event.category.startsWith('adcs_');
    });
    if (active.length) {
      rows.push(['Mode', active.map((e) => e.label).join(' / ')]);
    }

    host.replaceChildren();
    for (const [key, value, warn] of rows) {
      const dt = document.createElement('dt');
      dt.textContent = key;
      const dd = document.createElement('dd');
      dd.textContent = value;
      if (warn) dd.className = 'warn';
      host.append(dt, dd);
    }
  }

  // --------------------------------------------------------------------------- diagnostics

  private async showDiagnostics(): Promise<void> {
    const dialog = document.getElementById('dialog') as HTMLDialogElement;
    const body = document.getElementById('dialog-body')!;
    const satName = this.spacecraft.find((s) => s.key === this.primary)?.name ?? this.primary;
    document.getElementById('dialog-title')!.textContent = `Diagnostics — ${satName}`;
    body.innerHTML = '<p>Running…</p>';
    dialog.showModal();

    const [mdb, diagnosis] = await Promise.all([
      api.mdbStatus().catch((e) => ({ error: String(e) }) as { error: string }),
      api.diagnose(this.primary, this.window).catch((e) => ({ error: String(e) }) as { error: string }),
    ]);

    const parts: string[] = [];
    parts.push('<h3>Quaternion convention, measured from the data</h3>');
    if ('error' in diagnosis) {
      parts.push(`<p>Could not run: ${escapeHtml(String(diagnosis.error))}</p>`);
    } else {
      parts.push(
        '<p>The estimator reports the quaternion and the Euler angles for the same rotation, so the component order and the Euler sequence can be measured rather than assumed. The fine sun sensor then settles whether that rotation is ORC-referenced, which RPY alone cannot.</p>',
      );
      parts.push(
        `<p><strong>Configured:</strong> <code>${escapeHtml(
          `${diagnosis.configured.component_order} / ${diagnosis.configured.direction} / ${diagnosis.configured.euler_sequence}`,
        )}</code></p>`,
      );
      parts.push(rpyCheckCard(diagnosis.rpy_cross_check));
      parts.push(sunCheckCard(diagnosis.sun_sensor_check));
    }

    parts.push('<h3>Mission database reconciliation</h3>');
    if ('error' in mdb) {
      parts.push(`<p>${escapeHtml(String(mdb.error))}</p>`);
    } else if (!mdb.available) {
      parts.push(`<p>${escapeHtml(mdb.hint ?? 'The mission database has not been introspected.')}</p>`);
    } else {
      parts.push(
        `<p><span class="verdict ${mdb.ok ? 'verdict-ok' : 'verdict-warn'}">${
          mdb.ok ? 'consistent' : 'mismatches'
        }</span> ${mdb.parameter_count ?? 0} parameters known.</p>`,
      );
      if (mdb.warnings?.length) {
        parts.push(
          `<ul>${mdb.warnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`,
        );
      }
    }
    body.innerHTML = parts.join('\n');
  }

  // --------------------------------------------------------------------------------- misc

  private resize(): void {
    this.body.resize();
    this.bands.resize();
    this.strips.resize();
  }

  private toast(message: string, kind: 'info' | 'error' | 'busy' = 'info', timeout = 4000): () => void {
    const host = document.getElementById('status')!;
    const el = document.createElement('div');
    el.className = 'toast';
    el.dataset.kind = kind;
    el.textContent = message;
    host.appendChild(el);
    const remove = (): void => el.remove();
    if (timeout > 0) window.setTimeout(remove, timeout);
    return remove;
  }
}

// ------------------------------------------------------------------------------- helpers

function toVector(v: number[]): THREE.Vector3 {
  return new THREE.Vector3(v[0] ?? 0, v[1] ?? 0, v[2] ?? 0);
}

function defaultWindow(): TimeWindow {
  // The last 24 hours, rounded down to the minute. Deliberately relative rather than a fixed
  // date: a hardcoded window silently goes stale, and one that predates the TLE archive makes
  // every panel fail at once with no hint that the time range is the problem.
  const stop = roundToMinute(new Date());
  const start = new Date(stop.getTime() - 24 * 3600_000);
  return { start, stop };
}

function roundToMinute(date: Date): Date {
  return new Date(Math.floor(date.getTime() / 60_000) * 60_000);
}

/** Keep CZML sample counts sane: about 3000 samples across the window. */
function chooseStep(win: TimeWindow): number {
  const seconds = (win.stop.getTime() - win.start.getTime()) / 1000;
  return Math.min(600, Math.max(5, Math.round(seconds / 3000 / 5) * 5 || 5));
}

/** Format a Date for a datetime-local input using its UTC components. */
function toUtcInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(
    date.getUTCHours(),
  )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

/**
 * Parse a datetime-local value as UTC. `new Date(value)` would parse it as *local* time and
 * silently shift the window by the browser's UTC offset (handover 4.1); constructing from the
 * components with Date.UTC is exact in every timezone. Returns null on invalid input.
 */
function parseUtcInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(
    value.trim(),
  );
  if (!match) return null;
  const [, y, month, day, hour, minute, second] = match;
  const date = new Date(
    Date.UTC(Number(y), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second ?? 0)),
  );
  // Date.UTC rolls overflow forward (month 13, day 32, …); reject anything that moved.
  if (
    date.getUTCFullYear() !== Number(y) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day) ||
    date.getUTCHours() !== Number(hour) ||
    date.getUTCMinutes() !== Number(minute)
  ) {
    return null;
  }
  return date;
}

function formatError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.detail ? `${error.message} — ${error.detail}` : error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

interface CzmlFetch {
  document: object;
  notes: string[];
}

/**
 * Fetch the CZML document in the app (not via Cesium) so any `notes` properties on its packets
 * can be shown as caveats. The document is handed to Cesium parsed; the only external reference
 * it carries is the model URI, which is server-absolute, so nothing re-resolves against the
 * wrong base.
 */
async function fetchCzml(url: string): Promise<CzmlFetch> {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal: requestSignal(),
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail ?? JSON.stringify(body);
    } catch {
      /* keep the status text */
    }
    throw new ApiError(`/czml failed (${response.status})`, response.status, detail);
  }
  const data = (await response.json()) as unknown;
  const notes: string[] = [];
  if (Array.isArray(data)) {
    for (const packet of data) {
      const maybeNotes = (packet as { notes?: unknown } | null)?.notes;
      if (Array.isArray(maybeNotes)) {
        for (const note of maybeNotes) {
          if (typeof note === 'string' && note.trim()) notes.push(note);
        }
      }
    }
  }
  return { document: data as object, notes };
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function verdictPill(confident: boolean | undefined): string {
  return `<span class="verdict ${confident ? 'verdict-ok' : 'verdict-warn'}">${
    confident ? 'confident' : 'inconclusive'
  }</span>`;
}

function definitionList(rows: [string, string][]): string {
  return `<dl class="diag-grid">${rows
    .map(([key, value]) => `<dt>${escapeHtml(key)}</dt><dd>${escapeHtml(value)}</dd>`)
    .join('')}</dl>`;
}

/** Structured card for the RPY cross-check: measured fields, not a raw text dump. */
function rpyCheckCard(check: RpyCrossCheck): string {
  if (check.error) {
    return `<section class="diag-card"><h4>RPY cross-check</h4><p>${escapeHtml(check.error)}</p></section>`;
  }
  const rows: [string, string][] = [];
  if (check.component_order) rows.push(['Component order', check.component_order]);
  if (check.euler_sequence) rows.push(['Euler sequence', check.euler_sequence]);
  if (typeof check.quat_matches_rpy_directly === 'boolean') {
    rows.push(['Quaternion matches RPY directly', check.quat_matches_rpy_directly ? 'yes' : 'no']);
  }
  if (typeof check.residual_deg === 'number') {
    rows.push(['Best residual', `${check.residual_deg.toFixed(4)} deg`]);
  }
  if (typeof check.runner_up_residual_deg === 'number') {
    rows.push(['Runner-up residual', `${check.runner_up_residual_deg.toFixed(4)} deg`]);
  }
  if (typeof check.n_samples === 'number') rows.push(['Samples', String(check.n_samples)]);

  return `<section class="diag-card"><h4>RPY cross-check ${verdictPill(check.confident)}</h4>${
    rows.length ? definitionList(rows) : ''
  }${check.summary ? `<p class="diag-summary">${escapeHtml(check.summary)}</p>` : ''}</section>`;
}

/** Structured card for the sun-sensor check, including the ranked hypothesis table. */
function sunCheckCard(check: SunSensorCheck): string {
  if (check.error) {
    return `<section class="diag-card"><h4>Sun-sensor check</h4><p>${escapeHtml(check.error)}</p></section>`;
  }
  const parts: string[] = [
    `<section class="diag-card"><h4>Sun-sensor check ${verdictPill(check.confident)}</h4>`,
  ];

  if (check.best) {
    const best = check.best;
    parts.push(
      definitionList([
        ['Best hypothesis', `${best.component_order} / ${best.direction} / ${best.reference}`],
        ['Median sun-sensor residual', `${best.median_angle_deg.toFixed(3)} deg`],
        ['90th percentile', `${best.p90_angle_deg.toFixed(3)} deg`],
        ['Usable sunlit samples', String(best.n_usable)],
      ]),
    );
  }

  if (check.reason) {
    parts.push(`<p class="diag-summary">${escapeHtml(check.reason)}</p>`);
  }

  if (check.ranked && check.ranked.length > 0) {
    const rows = check.ranked
      .slice(0, 8)
      .map(
        (hypothesis, index) => `<tr${index === 0 ? ' class="diag-best"' : ''}>
          <td><code>${escapeHtml(
            `${hypothesis.component_order} / ${hypothesis.direction} / ${hypothesis.reference}`,
          )}</code></td>
          <td>${hypothesis.median_angle_deg.toFixed(3)}</td>
          <td>${hypothesis.p90_angle_deg.toFixed(3)}</td>
          <td>${hypothesis.n_usable}</td>
        </tr>`,
      )
      .join('');
    parts.push(
      `<table class="diag-table"><thead><tr><th>Hypothesis (order / direction / reference)</th><th>Median deg</th><th>p90 deg</th><th>Samples</th></tr></thead><tbody>${rows}</tbody></table>`,
    );
  }

  parts.push('</section>');
  return parts.join('');
}
